"use client";

import { useId } from "react";

// The star shape already shipped in public/star-s-fill.svg (16x16 viewBox), so
// the flow's stars match the ones on the marketing pages.
const STAR_PATH =
  "M8.00092 11L4.47412 13.154L5.43292 9.13405L2.29492 6.44605L6.41392 6.11605L8.00092 2.30005L9.58792 6.11605L13.7075 6.44605L10.5689 9.13405L11.5277 13.154L8.00092 11Z";

const FILLED = "#FFD400";
const EMPTY = "#D6E3E5";

// STAR_GAP is the distance between star origins, and is deliberately smaller
// than STAR_SIZE: the 16x16 path carries ~2.3 units of empty margin per side,
// so cells have to overlap for the stars to sit close together. Visible gap
// works out to STAR_GAP - STAR_SIZE * 0.714.
const STAR_SIZE = 26;
const STAR_GAP = 21.5;
const SCALE = STAR_SIZE / 16;

/**
 * Star rating supporting halves — `rating` 4.5 renders four filled stars and
 * one half-filled, which a flat asset can't do without a second file per value.
 */
export function ReviewStars({
  rating = 4.5,
  count = 5,
  className,
}: {
  rating?: number;
  count?: number;
  className?: string;
}) {
  const gradientId = `${useId()}-half-star`;

  return (
    <svg
      viewBox={`0 0 ${STAR_GAP * (count - 1) + STAR_SIZE} ${STAR_SIZE}`}
      className={className}
      role="img"
      aria-label={`${rating} out of ${count} stars`}
    >
      <defs>
        <linearGradient id={gradientId}>
          <stop offset="50%" stopColor={FILLED} />
          <stop offset="50%" stopColor={EMPTY} />
        </linearGradient>
      </defs>
      {Array.from({ length: count }, (_, i) => {
        const remaining = rating - i;
        const fill =
          remaining >= 1 ? FILLED : remaining >= 0.5 ? `url(#${gradientId})` : EMPTY;

        return (
          <path
            key={i}
            d={STAR_PATH}
            fill={fill}
            transform={`translate(${i * STAR_GAP} 0) scale(${SCALE})`}
          />
        );
      })}
    </svg>
  );
}
